import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {
  requestUserLogin,
  requestPasswordResetEmail
} from "actions/authentication";
import { selectors } from "reducers";
import Disclaimer from "./disclaimer";
import AzureAdSSO, { getAzureClientIdFromUrl } from "./azure-ad-sso";
import PasswordInput from "components/ui_new/input/password-input";
import Input from "components/ui_new/input";
import logo from "assets/images/S3_WellSky_color.png";
import Button from "components/ui_new/button";

const Login = ({
  isRequestingResetPasswordEmail,
  loginError,
  requestUserLogin,
  requestPasswordResetEmail,
  isSubmitting,
  retryUntil
}) => {
  const [mode, setMode] = React.useState(() =>
    getAzureClientIdFromUrl() ? "SSO" : "Login"
  );
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [countdown, setCountdown] = React.useState(0);

  React.useEffect(() => {
    if (!retryUntil) {
      setCountdown(0);
      return;
    }
    const tick = () => {
      const remaining = Math.ceil((retryUntil - Date.now()) / 1000);
      setCountdown(remaining > 0 ? remaining : 0);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [retryUntil]);

  function handleLogin(e) {
    e.preventDefault();
    requestUserLogin(email, password, window.device_token);
  }

  function handleForgotPassword(e) {
    e.preventDefault();
    requestPasswordResetEmail({ email });
  }
  return (
    <React.Fragment>
      <section id="login_screen" className="new-design">
        <div className="login-background"></div>
        <div className="login-screen-header">
          <img className="login-logo" src={logo} alt="Logo" />
        </div>

        <div className="login-container">
          <h2 className="welcome-heading">Welcome</h2>

          {mode == "Login" && (
            <div className="login-form-card">
              <form acceptCharset="UTF-8" onSubmit={handleLogin}>
                <Input
                  label="Email"
                  id="email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={({ target }) => setEmail(target.value)}
                  placeholder="Enter your email address"
                  required
                  aria-required="true"
                />

                <PasswordInput
                  label="Password"
                  id="password"
                  name="password"
                  value={password}
                  onChange={({ target }) => setPassword(target.value)}
                  placeholder="Enter your password"
                  required
                  aria-required="true"
                />

                <div className="login-info">
                  {!isSubmitting && !loginError && countdown === 0 && <div>&nbsp;</div>}
                  <div className="login-error">
                    {!isSubmitting && countdown > 0
                      ? `Too many login attempts. Please wait ${countdown} second${countdown !== 1 ? "s" : ""} before trying again.`
                      : !isSubmitting && !retryUntil && loginError}
                  </div>
                  <div className="login-message">
                    {isSubmitting && "Submitting..."}
                  </div>
                </div>

                <div className="login-form-actions">
                  <Button type="submit" variant="primary" disabled={countdown > 0}>
                    Login
                  </Button>

                  <div className="login-links">
                    <Button
                      type="button"
                      variant="link"
                      onClick={() => setMode("ForgotPassword")}
                    >
                      Forgot password?
                    </Button>

                    <Button
                      type="button"
                      variant="link"
                      onClick={() =>
                        (window.location.href =
                          "https://sleepsolutionsandservices.com/terms-of-use")
                      }
                    >
                      Terms of Use
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          )}

          {mode == "SSO" && (
            <div className="login-form-card">
              <div className="login-form-actions">
                <AzureAdSSO
                  setEmail={setEmail}
                  setPassword={setPassword}
                  requestUserLogin={requestUserLogin}
                />
                <div className="login-links">
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => setMode("ForgotPassword")}
                  >
                    Forgot password?
                  </Button>
                  <Button
                    type="button"
                    variant="link"
                    onClick={() =>
                      (window.location.href =
                        "https://sleepsolutionsandservices.com/terms-of-use")
                    }
                  >
                    Terms of Use
                  </Button>
                </div>
              </div>
            </div>
          )}

          {mode === "ForgotPassword" && (
            <div className="login-form-card">
              <div className="login-form-heading">Forgot your password?</div>
              <form acceptCharset="UTF-8" onSubmit={handleForgotPassword}>
                <Input
                  label="Email"
                  id="email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={({ target }) => setEmail(target.value)}
                  placeholder="Enter your email address"
                  required
                  aria-required="true"
                />
                <div className="login-form-actions">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isRequestingResetPasswordEmail}
                  >
                    {isRequestingResetPasswordEmail
                      ? "Sending Email"
                      : "Send me reset password instructions"}
                  </Button>

                  <div className="login-links">
                    <Button
                      type="button"
                      variant="link"
                      onClick={() => setMode("Login")}
                    >
                      Sign in
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          )}
          <Disclaimer />
        </div>
      </section>
    </React.Fragment>
  );
};

export default connect(
  state => ({
    loginError: selectors.getLoginError(state),
    isSubmitting: selectors.getIsLoginSubmitting(state),
    retryUntil: selectors.getRetryUntil(state),
    resetEmailFailure: state.getIn([
      "fetch",
      "SEND_RESET_PASSWORD_EMAIL",
      "didFail"
    ]),
    isRequestingResetPasswordEmail: state.getIn([
      "fetch",
      "SEND_RESET_PASSWORD_EMAIL",
      "isFetching"
    ])
  }),
  {
    requestUserLogin,
    requestPasswordResetEmail
  }
)(Login);

Login.propTypes = {
  requestUserLogin: PropTypes.func.isRequired,
  requestPasswordResetEmail: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  loginError: PropTypes.string,
  isRequestingResetPasswordEmail: PropTypes.bool,
  retryUntil: PropTypes.number
};
